import { useEffect, useState } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Loader2, BarChart3, CheckCircle2, Clock, AlertTriangle, Zap } from "lucide-react";
import api from "../lib/apiClient";
import { CATEGORY_META } from "../lib/gameData";

const RANGES = [{ k: "7d", l: "7 j" }, { k: "30d", l: "30 j" }, { k: "90d", l: "90 j" }];

export default function Analytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30d");

  useEffect(() => { api.get(`/analytics?range=${range}`).then((r) => setData(r.data)).catch(() => {}); }, [range]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-amber-500 animate-spin" /></div>;
  const scoreData = (data.score_history || []).map((h) => ({ day: h.day.slice(5), score: h.score }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold flex items-center gap-2"><BarChart3 className="w-7 h-7 text-amber-400" /> Analytics</h1>
          <p className="text-zinc-500 text-sm mt-1">Tes résultats réels : régularité, ponctualité, progression.</p>
        </div>
        <div className="flex gap-1 bg-zinc-900 rounded-full p-1">
          {RANGES.map((r) => (
            <button key={r.k} onClick={() => setRange(r.k)} data-testid={`analytics-range-${r.k}`}
              className={`px-3 h-8 rounded-full text-xs font-medium ${range === r.k ? "gold-gradient text-black" : "text-zinc-400 hover:text-zinc-100"}`}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
        <Kpi icon={CheckCircle2} label="Taux de complétion" value={`${data.completion_rate}%`} />
        <Kpi icon={Clock} label="Ponctualité" value={data.on_time_rate != null ? `${data.on_time_rate}%` : "—"} />
        <Kpi icon={Zap} label={`XP (${range})`} value={data.xp_period} />
        <Kpi icon={AlertTriangle} label="En retard" value={data.overdue_count} danger={data.overdue_count > 0} />
      </div>

      <Panel title="Activité (tâches terminées / jour)" testid="analytics-activity">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35" />
              <XAxis dataKey="day" stroke="#71717A" fontSize={10} interval="preserveStartEnd" />
              <YAxis stroke="#71717A" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0F0F13", border: "1px solid #2A2A35", borderRadius: 12 }} />
              <Bar dataKey="tasks" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <Panel title="Évolution du Score de progression" testid="analytics-score">
        {scoreData.length < 2 ? <p className="text-sm text-zinc-500 py-8 text-center">Pas encore assez de données.</p> : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={scoreData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2A2A35" />
                <XAxis dataKey="day" stroke="#71717A" fontSize={10} />
                <YAxis stroke="#71717A" fontSize={11} domain={[0, 1000]} />
                <Tooltip contentStyle={{ background: "#0F0F13", border: "1px solid #2A2A35", borderRadius: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>

      <div className="grid gap-4 sm:grid-cols-2">
        <Panel title="Projets par statut" testid="analytics-projects">
          <div className="space-y-2 mt-1">
            {Object.entries(data.projects_by_status).length === 0 ? <p className="text-sm text-zinc-500">Aucun projet.</p> :
              Object.entries(data.projects_by_status).map(([s, n]) => (
                <div key={s} className="flex items-center justify-between text-sm"><span className="text-zinc-300">{s}</span><span className="font-mono text-amber-400">{n}</span></div>
              ))}
          </div>
        </Panel>
        <Panel title="Forces par domaine" testid="analytics-categories">
          <div className="space-y-2 mt-1">
            {data.categories.map((c) => (
              <div key={c.key} className="flex items-center gap-2">
                <span className="text-xs text-zinc-400 w-24 shrink-0">{CATEGORY_META[c.key]?.label || c.key}</span>
                <div className="flex-1 h-2 rounded-full bg-zinc-800 overflow-hidden"><div className="h-full" style={{ width: `${c.score}%`, backgroundColor: CATEGORY_META[c.key]?.color || "#F59E0B" }} /></div>
                <span className="font-mono text-xs text-zinc-400 w-8 text-right">{c.score}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, danger }) {
  return (
    <div className={`bg-[#18181E] border rounded-2xl p-4 ${danger ? "border-red-500/40" : "border-zinc-800"}`}>
      <Icon className={`w-5 h-5 mb-2 ${danger ? "text-red-400" : "text-amber-400"}`} />
      <p className="font-display text-2xl font-extrabold">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-0.5">{label}</p>
    </div>
  );
}
function Panel({ title, children, testid }) {
  return (
    <div className="bg-[#18181E] border border-zinc-800 rounded-3xl p-5" data-testid={testid}>
      <h2 className="font-display text-lg font-bold mb-3">{title}</h2>
      {children}
    </div>
  );
}
